import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MediaGrid from '@/components/feature/MediaGrid';
import { getHotMedia, searchAll } from '@/lib/media';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') || '';
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const result = useMemo(() => searchAll(keyword), [keyword]);
  const totalCount = result.movies.length + result.tvs.length + result.news.length;
  const hasKeyword = keyword.trim().length > 0;
  const hotMedia = getHotMedia(6);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { q: value } : {});
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />

      <div className="mt-4 mb-6">
        <h1 className="text-2xl font-bold text-foreground-950 font-heading md:text-3xl">搜索结果</h1>
        {hasKeyword && (
          <p className="mt-2 text-sm text-foreground-500">
            关键词：<span className="font-semibold text-accent-600">{keyword}</span> · 共找到 {totalCount} 条相关内容
          </p>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-3 rounded-lg bg-background-100 p-4 sm:flex-row sm:items-center"
      >
        <div className="flex flex-1 items-center gap-2 rounded-md bg-background-50 px-3 py-2.5 ring-1 ring-background-200">
          <i className="ri-search-line text-base text-foreground-400" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入影片、剧集或资讯关键词"
            className="w-full bg-transparent text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
        >
          搜索
        </button>
      </form>

      {!hasKeyword ? (
        <div className="mt-10">
          <h2 className="mb-4 text-lg font-bold text-foreground-950 font-heading">大家都在看</h2>
          <MediaGrid items={hotMedia} columns="grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" />
        </div>
      ) : totalCount === 0 ? (
        <div className="mt-8 flex flex-col items-center justify-center rounded-lg bg-background-100 py-16 text-center">
          <i className="ri-search-eye-line text-4xl text-foreground-300" />
          <p className="mt-3 text-sm font-medium text-foreground-700">未找到相关内容</p>
          <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或看看下面的热门推荐</p>
          <div className="mt-8 w-full px-4 text-left">
            <h2 className="mb-4 text-lg font-bold text-foreground-950 font-heading">热门影视推荐</h2>
            <MediaGrid items={hotMedia} columns="grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" />
          </div>
        </div>
      ) : (
        <div className="mt-8 flex flex-col gap-10">
          {result.movies.length > 0 && (
            <section>
              <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950 font-heading">
                <i className="ri-film-line text-accent-600" />
                电影
                <span className="text-sm font-normal text-foreground-500">（{result.movies.length}）</span>
              </h2>
              <MediaGrid items={result.movies} columns="grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" />
            </section>
          )}

          {result.tvs.length > 0 && (
            <section>
              <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950 font-heading">
                <i className="ri-tv-2-line text-accent-600" />
                电视剧
                <span className="text-sm font-normal text-foreground-500">（{result.tvs.length}）</span>
              </h2>
              <MediaGrid items={result.tvs} columns="grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" />
            </section>
          )}

          {result.news.length > 0 && (
            <section>
              <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950 font-heading">
                <i className="ri-newspaper-line text-accent-600" />
                影视资讯
                <span className="text-sm font-normal text-foreground-500">（{result.news.length}）</span>
              </h2>
              <div className="flex flex-col gap-3">
                {result.news.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/${item.id}`}
                    className="group flex gap-4 rounded-lg bg-background-50 p-4 ring-1 ring-background-200/70 transition-all duration-300 hover:ring-accent-300"
                  >
                    <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title} - 八戒影院影视资讯`}
                        className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs text-foreground-500">
                        <span className="text-accent-600">{item.category}</span>
                        <span>{item.date}</span>
                      </div>
                      <h3 className="mt-1 line-clamp-2 text-sm font-semibold text-foreground-950 transition-colors group-hover:text-accent-600">
                        {item.title}
                      </h3>
                      <p className="mt-1 line-clamp-1 text-xs text-foreground-500">{item.summary}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}